import { Component, Inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { CreateRealizationTime, FTimeControls, RealizationTime, RealizationTimeConfig } from '../../../_models/realization-time.model';
import { AuthenticationService } from '@panel/auth';
import { TimeService } from '../../../_services/time.service';
import { DictionaryValues } from '../../../dictionary/dictionary.model';
import { DictionaryService } from '../../../dictionary/dictionary.service';
import { User } from '../../../user/user.model';
import { UserService } from '../../../user/user.service';
import { HeaderConfig } from '../../dialog-header/dialog-header.model';
import { TranslateService } from '@ngx-translate/core';
import { TimePerm } from '../../../_enums/permission.enum';

@Component({
  selector: 'app-realization-time-add',
  templateUrl: './realization-time-add.component.html',
  styleUrls: ['./realization-time-add.component.scss']
})

export class RealizationTimeAddComponent {

  fControl = FTimeControls;
  realizationTimeForm: FormGroup;

  currentUser: User = null;
  userList: User[] = [];
  defaultDay: Date = new Date();
  errorTime: boolean = false;
  categoryPerm: boolean = false;
  descriptionPerm: boolean = false;
  dateEndManuallyChanged: boolean = false;

  newTimeRealization: CreateRealizationTime = new CreateRealizationTime();

  headerConfig: HeaderConfig = {
    ... new HeaderConfig(),
    headerText: (this.data?.idTime && this.data?.readonly) 
      ? 'Szczegóły czasu realizacji' 
      : 'Zarządzanie czasem realizacji',
    cancelButton: true,
  }

  subscription: Subscription = new Subscription();
 
  constructor(
    public dialogRef: MatDialogRef<RealizationTimeAddComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: RealizationTimeConfig,
    public userServ: UserService,
    public dictionaryServ: DictionaryService,
    private authServ: AuthenticationService,
    private timeServ: TimeService,
    private snackBar: MatSnackBar,
    private translateServ: TranslateService
  ) { }

  async ngOnInit() {
    this.categoryPerm = await this.userServ.hasRolesAsync([TimePerm.CATEGORY_MANAGE]);
    this.descriptionPerm = await this.userServ.hasRolesAsync([TimePerm.DESCRIPTION_MANAGE]);

    if(this.data.currentUser) {
      this.currentUser = this.authServ.currentUserValue;
    };

    if(this.data.idTime) {
      this.loadTimeDetails();
    } else {
      this.newTimeRealization = { ...this.newTimeRealization, ...this.data }
      this.initForm(null);
    }
  }

  compareWithFn(
    users: User, 
    selectedUser: User
  ) {
    return users && selectedUser && users.id === selectedUser.id;
  }

  compareTypesFn(
    types: DictionaryValues, 
    selectedType: DictionaryValues
  ) {
    return (types && selectedType && (types == selectedType)) || (types === selectedType);
  }

  onDismiss() {
    this.dialogRef.close(false);
  }

  /** Sets the end date to the start date as long as the user hasn't picked a different one themselves */
  onDateStartChange(newDate: Date) {
    if (this.dateEndManuallyChanged) { return; }

    this.realizationTimeForm.controls[FTimeControls.DATE_END].setValue(newDate, { emitEvent: false });
    this.getCalculateTime();
  }

  onDateEndChange() {
    this.dateEndManuallyChanged = true;
    this.getCalculateTime();
  }

  getCalculateTime() {
    const start = this.realizationTimeForm.controls[FTimeControls.TIME_START].value;
    const end = this.realizationTimeForm.controls[FTimeControls.TIME_END].value;
    const dateStart = this.realizationTimeForm.controls[FTimeControls.DATE].value;
    const dateEnd = this.realizationTimeForm.controls[FTimeControls.DATE_END].value ?? dateStart;

    if (!start || !end || !dateStart) return;

    // Combine the realization dates with their times so overnight ranges are calculated correctly
    const startTime = this.combineDateAndTime(dateStart, start);
    const endTime = this.combineDateAndTime(dateEnd, end);

    // Calculate the difference in milliseconds
    const diffInMs = endTime.getTime() - startTime.getTime();

    // Convert the difference to hours and minutes
    const diffInHours = Math.floor(diffInMs / 1000 / 60 / 60);
    const diffInMinutes = Math.floor((diffInMs / 1000 / 60) % 60);

    // Format the result as a string
    const diffFormatted = `${diffInHours < 10 ? '0' : ''}${diffInHours}:${diffInMinutes < 10 ? '0' : ''}${diffInMinutes}`;

    // Patch the form control with the formatted difference
    this.realizationTimeForm.controls[FTimeControls.DIFF_TIME].patchValue(diffFormatted);

    return diffFormatted;
  }

  /** Creates the time realization */
  onConfirm() {
    if(!this.setUpTimeRealization()) return;

    if(this.data.deferPersist) {
      this.dialogRef.close(this.newTimeRealization);
      return;
    }

    this.subscription.add(
      this.timeServ.createTimeRealization(this.newTimeRealization).subscribe(
        (time) => {
          this.openSnack('Dodano czas realizacji', 'custom-snack-bar__success')
         },
        (err) => {
          const msg = err?.error?.code 
            ? ('Nie dodano czasu realizacji - ' + this.translateServ.instant(err.error.code)) 
            : 'Nie dodano czasu realizacji';

          this.openSnack(msg, 'custom-snack-bar__error')
        },
        () => {
          this.dialogRef.close(true);
        },
      )
    );
  }

  /** Updates the time realization */
  onUpdate() {
    if(!this.setUpTimeRealization()) return;

    this.subscription.add(
      this.timeServ.updateTimeRealization(this.data.idTime, this.newTimeRealization).subscribe(
        (time) => {
          this.openSnack('Zapisano czas realizacji', 'custom-snack-bar__success')
         },
        (err) => {
          this.dialogRef.close(false);
          this.openSnack('Nie zaktualizowano czasu realizacji', 'custom-snack-bar__error')
        },
        () => {
          this.dialogRef.close(true);
        },
      )
    );
  }

  /**
   * Validates the form and sets up the newTimeRealization object
   * @returns boolean - if the form is valid or not
   */
  private setUpTimeRealization() {
    if( !this.checkFormValidation() ) {
      return false;
    }

    this.newTimeRealization = {...this.newTimeRealization,...this.realizationTimeForm.value};
    this.newTimeRealization.idUser = this.realizationTimeForm.controls.user.value.id;

    // Set the dates to the end of the day to prevent timezone issues
    if(this.data.idTime) {
      this.newTimeRealization.date = new Date(this.newTimeRealization?.date);
      this.newTimeRealization.dateEnd = new Date(this.newTimeRealization?.dateEnd);
    }
    this.newTimeRealization?.date?.setHours(23, 59);
    this.newTimeRealization?.dateEnd?.setHours(23, 59);

    // If drive is not checked, set timeDrive to null
    if(!(this.realizationTimeForm.controls[FTimeControls.DRIVE].value)) this.newTimeRealization.timeDrive = null;

    return true;
  }

  private initForm(time?: RealizationTime) {
    this.realizationTimeForm = new FormGroup({
      [FTimeControls.USER]: new FormControl({
        value: time?.user ?? this.currentUser, 
        disabled: (time && this.data.readonly) ? true : (!this.data.timeForAllUsers)
      }, [Validators.required]),
      [FTimeControls.DATE]: new FormControl({
        value: time?.date ?? this.defaultDay,
        disabled: (time && this.data.readonly) ? true : false
      }, [Validators.required]),
      // falls back to the start date so previously created entries (without a stored end date) keep displaying as single-day
      [FTimeControls.DATE_END]: new FormControl({
        value: time?.dateEnd ?? time?.date ?? this.defaultDay,
        disabled: (time && this.data.readonly) ? true : false
      }, [Validators.required]),
      [FTimeControls.TIME_START]: new FormControl({
        value: time?.timeStart ? time?.timeStart.substring(0,5) : null,
        disabled: (time && this.data.readonly) ? true : false
      },[Validators.required]),
      [FTimeControls.TIME_END]: new FormControl({
        value: time?.timeEnd ? time?.timeEnd.substring(0,5) : null,
        disabled: (time && this.data.readonly) ? true : false
      },[Validators.required]),
      [FTimeControls.TYPE]: new FormControl({
        value: time ? time?.type : null,
        disabled: (time && this.data.readonly) ? true : false
      },[]),
      [FTimeControls.DESCRIPTION]: new FormControl({
        value: time?.description ?? '',
        disabled: (time && this.data.readonly) ? true : false
      },[Validators.maxLength(2000)]),
      [FTimeControls.DIFF_TIME]: new FormControl({
        value: null, 
        disabled: true
      }, []),
      [FTimeControls.DRIVE]: new FormControl({
        value: time?.drive ?? false,
        disabled: (time && this.data.readonly) ? true : false
      }),
      [FTimeControls.TIME_DRIVE]: new FormControl({
        value: time?.timeDrive ?? '00:00',
        disabled: (time && this.data.readonly) ? true : false
      }),
      [FTimeControls.BLAME_ON]: new FormControl({
        value: time?.blameOn ?? null, 
        disabled: (time && this.data.readonly) ? true : (!this.data?.requireBlameOn)
      }, [Validators.required]),
    });

    if(this.categoryPerm) {
      this.realizationTimeForm.controls[FTimeControls.TYPE].setValidators([Validators.required]);
    }

    this.dateEndManuallyChanged = time?.dateEnd
      ? (new Date(time.dateEnd).toDateString() !== new Date(time.date).toDateString())
      : false;

    this.subscription.add(
      this.realizationTimeForm.controls[FTimeControls.DATE].valueChanges.subscribe(
        (newDate) => this.onDateStartChange(newDate)
      )
    );

    this.newTimeRealization = {...this.newTimeRealization,...this.realizationTimeForm.value};
  }

  /** Combines a calendar date with a time string (HH:mm) into a single Date */
  private combineDateAndTime(date: Date, time: string): Date {
    const [hours, minutes] = time.split(':').map(Number);
    const combined = new Date(date);

    combined.setHours(hours, minutes, 0, 0);

    return combined;
  }

  private checkFormValidation() {
    if(this.realizationTimeForm.invalid){
      this.realizationTimeForm.markAllAsTouched();
      return false;
    }

    const dateStart = this.realizationTimeForm.controls[FTimeControls.DATE].value;
    const dateEnd = this.realizationTimeForm.controls[FTimeControls.DATE_END].value;
    const timeStart = this.realizationTimeForm.controls[FTimeControls.TIME_START].value;
    const timeEnd = this.realizationTimeForm.controls[FTimeControls.TIME_END].value;

    // Compare the full start/end date-time so ranges crossing midnight (e.g. 22:00 -> 02:00 next day) are valid
    if (
      this.combineDateAndTime(dateStart, timeStart) >=
      this.combineDateAndTime(dateEnd, timeEnd)
    ) {
      this.errorTime = true;
      this.realizationTimeForm.controls[FTimeControls.DATE_END].markAsTouched();
      this.realizationTimeForm.controls.timeStart.markAsTouched();
      this.realizationTimeForm.controls.timeEnd.markAsTouched();
      return false;
    }

    this.errorTime = false;

    return true;
  }

  private openSnack(message, className) {
    this.snackBar.open(message, '', {
      duration: 1000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: ['custom-snack-bar', className]
    });
  }

  private loadTimeDetails() {
    this.subscription.add(
      this.timeServ.getTimeRealizationById(this.data.idTime).subscribe({
        next: (time: RealizationTime) => {
          this.initForm(time);
        },
        error: (err) => {
          this.dialogRef.close(false);
          this.openSnack('Nie udało się wczytać szczegółów czasu', 'custom-snack-bar__error')
        }, 
        complete: () => {
          this.getCalculateTime();
        }
      })
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

}
